/**
 * Every shop in Los Baños and Bay a rider could be sent to buy from — and, since 20 August
 * 2026, the buildings around UP Los Baños a rider could be sent to *deliver to*: the
 * campus itself, and the dorms in the belt around it.
 *
 * **921 rows.**
 *
 *     Restaurant 202 · Convenience store 181 · UPLB building 181 · Fast food 68
 *     Dorm / apartment 60 · Café 39 · Landmark 39 · Grocery 38 · Bakery 34 · Tindahan 34
 *     Botika 23 · Hardware 19 · Gas station 1 · Bank 1 · Health center 1
 *
 * ## Where these came from
 *
 * ### 637 shops, from two harvests
 *
 * The OpenStreetMap harvest described at `stores.osm-backup.js` and the Google Maps one
 * described below, merged. The counts inside those two sections are each accurate about
 * their own batch and **stale about the file** — they were written when each batch was the
 * whole of it.
 *
 * ### 239 campus rows, on 20 August 2026
 *
 * OpenStreetMap again, via the Overpass API. UPLB's campus polygon (`way/517120243`) was
 * pulled first, then every named `building`, `amenity`, `office`, `leisure` and `tourism`
 * feature in a bounding box over the two towns — **1069 of them** — and each was tested
 * against that polygon. **341 stood inside it.**
 *
 * Those were cut down twice. First by **what a rider can be sent to**: a college, a
 * dormitory, a lecture hall and the Carillon are all places somebody waits; a car park, a
 * bench, a greenhouse, a generator room and a row named *Unit 3* are not, however real.
 * That left 264. Then the same place tagged twice was folded — *UPLB College of
 * Development Communication* and *Nora C. Quebral Hall* share a pin to six decimals — and
 * twelve campus eateries already in the list were dropped rather than repeated.
 * **239 survived**, ten of them UPLB units the drawn polygon leaves outside its own line:
 * the Institute of Plant Breeding, BIOTECH, NCPC, the Forestry facilities, UPRHS.
 *
 * Every name and pin is OSM's, and the pin is the way's centre, not a geocode of an
 * address. Two exceptions, both noted where they are made: *Rural Economic Development and
 * Renewable Energy Cente* is completed to *Center*, and six rows whose OSM tag reads the
 * thing wrong — a dormitory tagged a viewpoint — carry a category read off the name.
 *
 * **`alias` starts here.** 69 of the 239 carry OSM's `alt_name`, because *ICS*, *PhySci*,
 * *NCAS* and *MaReHa* are not shortenings of those buildings' names — they are what the
 * buildings are called, and a search that cannot hear them is a search a student cannot
 * use. It is matched and scored like a name, and never displayed.
 *
 * ### 45 private dorms, on 20 August 2026
 *
 * The other half of a student's day. `scripts/harvest-dorms.mjs` — same bounding box, the
 * polygon test inverted, so the two passes cover the town exactly once between them.
 *
 * **685 named things stood outside the campus; 51 of them were somewhere to sleep.** Kept
 * on OSM's tag where it had one (`building=dormitory`, `building=apartments`,
 * `amenity=dormitory`, `tourism=apartment`) and on the name where it did not — half the
 * belt is `building=yes` and OSM knows only what the gate says. *Teodoro Dormitory* is not
 * ambiguous just because nobody typed a second tag.
 *
 * Six were dropped: IRRI's staff apartments, a row called *Student's Dorm*, and four
 * buildings all named *Juliano Apartments*, of which one is kept. **45 survived.**
 *
 * **`building=residential` and `building=house` are deliberately excluded.** They are the
 * subdivisions out towards Maahas, drawn lot by lot as *Block-11* and *Lot 9*, and a
 * suggestion list offering four rows called *Block* is worse than one offering none.
 *
 * **Hotels and resorts were not judged.** City of Springs, Soft Pillow, Mer-Nel's, Trace
 * Suites, BP International — all real, all named, none of them housing. They want their
 * own pass and their own chip.
 *
 * ### The Google Maps batch
 *
 * Google Maps, read out of the search results feed in a real browser, on 17 August 2026.
 * The seed was one shared search — *restaurants in los banos laguna philippines* — which
 * gave **74 places**, plus a handful more from a second pass. **79 rows survived** after
 * dropping two whose Google name was the bare word *Restaurant* and *Carinderia*: a rider
 * cannot ask for those at a door.
 *
 * **22 more were added by hand on 17 August 2026**, from Google Maps place links handed
 * over one at a time rather than harvested — the first rows here that are not eateries,
 * and so the first grocery, sari-sari, bakery and mall the search box has ever held.
 * Three of that batch were already in the harvest (*Angel's Pizza*, *Sweet Keish*, *The
 * Burger Garage*) and were not repeated. **101 rows now.**
 *
 *     restaurant 76 · fast food 10 · café 7 · convenience 3 · bakery 2 · grocery 2 · mall 1
 *
 * Every name, pin and street below is Google's. The pin is the `!3d…!4d…` pair out of each
 * result's or place's own link, not a geocode of the address. The category on the 22 is a
 * reading of the name — Google's own label disagrees on two of them, and is noted where it
 * does. Their street is Google's where the link carried one, and otherwise the street the
 * neighbouring rows already carry, or the bare municipality when nothing sat close enough.
 *
 * ## ⚠️ This list is incomplete, and knowingly so
 *
 * **Hours on 850 of the 921 are `null`.** OSM carried `opening_hours` on a minority, and
 * only the windows readable without guessing were taken — a split shift like *Mo-Fr
 * 08:00-12:00,13:00-16:30* is refused rather than flattened, and the CEM Library is
 * therefore listed with no hours at all. `isOpenNow` reads null as *unknown* rather than
 * open, which is the safe direction. A half-understood schedule is worse than none: it
 * puts a rider outside a door at an hour somebody can point at and call wrong.
 *
 * **The campus batch is one map's opinion of the campus.** OSM's coverage of UPLB is
 * unusually good and still not complete — a building nobody has drawn is a building this
 * cannot list, and the 264-to-239 cut was a judgement about what a rider can be sent to,
 * not a fact. The Overpass query and the two filter lists are worth keeping next to
 * whoever re-runs it.
 *
 * **The dorms are a floor, not a census.** OSM's coverage of UPLB itself is unusually
 * good; its coverage of the boarding houses behind Umali and along Ruby is not. The small
 * ones are simply absent, and a room rented in somebody's house is never going to be a
 * building on a map. 45 is what has been drawn, not what is there.
 *
 * **Eleven dorm rows carry a street they were not told.** OSM had no `addr:street`, so the
 * nearest named road within sixty metres was taken instead. It is what somebody would say
 * out loud and it is not on the building — the pin is OSM's own and is what the rider
 * follows; the street only tells two rows apart in the picker. Seven got neither and read
 * as the bare municipality.
 *
 * **The neighbours are not here.** IRRI, PCAARRD, UPOU and Trace College all sit within a
 * few minutes of the campus and none of them is UPLB, so none of them was taken. Nor were
 * the hotels and resorts. Both are real dropoffs and both want their own pass.
 *
 * ## What it is for
 *
 * It is **not a screen**. Nobody scrolls the whole list: it sits behind the search box and
 * surfaces only what somebody typed towards. It answers in the browser, instantly and for
 * nothing, and Nominatim is asked only for what it does not hold.
 *
 * ## Editing
 *
 * One line per shop, in the tuple below. A blank area string, `null` hours and a missing
 * seventh entry are all fine. ⚠️ Do not leave a half-written row — a missing value in the
 * middle is a syntax error and it takes the whole app down, not just this list.
 *
 * @typedef {object} Store
 * @property {string} storeName — what the rider asks for on arrival.
 * @property {string} category  — a key from `CATEGORIES`. Shown, and searched.
 * @property {number} latitude
 * @property {number} longitude
 * @property {string} area      — the street, to tell four Jollibees apart. May be empty.
 * @property {string} alias     — other names for the same place, `; `-separated. Searched,
 *   never shown. `ICS`, `PhySci`, `MaReHa` — what a student types, and none of it is in
 *   the name. Empty on everything that is not a campus row.
 * @property {Record<string, {opens: string, closes: string}|null>|null} hours — per day,
 *   `mon` to `sun`, 24-hour Manila time. A null day is shut; null hours are unknown.
 */

/**
 * What kind of shop it is.
 *
 * Two jobs, and the second is what earns it. It labels the row — *Fast food* says more
 * than *Tindahan*, which every row used to be. And it is **searched**: somebody typing
 * `kape` finds the cafés without knowing which of the 217 are cafés.
 *
 * `search` carries the words people actually type, in both languages. A category findable
 * only by its English name is findable only by people who did not need it.
 */
export const CATEGORIES = {
  fastfood: { label: 'Fast food', search: ['fast food', 'fastfood', 'kainan'] },
  restaurant: { label: 'Restaurant', search: ['restaurant', 'resto', 'kainan'] },
  cafe: { label: 'Café', search: ['cafe', 'kape', 'coffee', 'milk tea'] },
  pharmacy: { label: 'Botika', search: ['botika', 'pharmacy', 'gamot', 'drugstore'] },
  grocery: { label: 'Grocery', search: ['grocery', 'palengke', 'supermarket'] },
  convenience: { label: 'Convenience store', search: ['convenience', 'sari-sari', 'sari sari'] },
  bakery: { label: 'Bakery', search: ['bakery', 'panaderya', 'tinapay'] },
  hardware: { label: 'Hardware', search: ['hardware', 'ferreteria', 'konstruksyon'] },

  /**
   * ## The second batch
   *
   * Nine categories covered what a rider *buys from*. These twelve cover where a rider is
   * *sent* — a bank, a barangay hall and a health centre sell nothing, but they are real
   * destinations somebody types into the picker, and leaving them out means the search box
   * answers with silence.
   *
   * Same rule as above: `search` carries what people actually type, Tagalog first where
   * Tagalog is what they would reach for.
   */
  market: { label: 'Palengke', search: ['palengke', 'public market', 'talipapa', 'pamilihan'] },
  laundry: { label: 'Labahan', search: ['labahan', 'laundry', 'laundromat', 'labada'] },
  water: { label: 'Water refilling', search: ['water refilling', 'tubig', 'water station', 'purified'] },
  gadget: { label: 'Cellphone repair', search: ['cellphone repair', 'gadget', 'repair', 'palit screen', 'cp repair'] },
  mall: { label: 'Mall', search: ['mall', 'shopping'] },
  department: { label: 'Department store', search: ['department store', 'dept store', 'damit'] },
  bank: { label: 'Bank', search: ['bank', 'banko', 'atm'] },
  pawnshop: { label: 'Sanglaan', search: ['sanglaan', 'pawnshop', 'prenda'] },
  remittance: { label: 'Padala', search: ['padala', 'remittance', 'pera padala', 'money transfer'] },
  barangay: { label: 'Barangay hall', search: ['barangay hall', 'brgy', 'barangay', 'kapitan'] },
  health: { label: 'Health center', search: ['health center', 'clinic', 'klinika', 'ospital', 'rhu'] },
  flowers: { label: 'Flower shop', search: ['flower shop', 'bulaklak', 'florist', 'bouquet'] },
  pets: { label: 'Pet shop', search: ['pet shop', 'alagang hayop', 'aso', 'pusa', 'vet'] },

  /**
   * ## The third batch — the campus
   *
   * Everything above answers *where does the rider buy it*. These three answer **where
   * does the rider bring it**, which until now the box could not answer at all inside
   * UPLB. A dropoff on campus is not a street address — it is *ICS*, *MaReHa*, *Freedom
   * Park* — and typing any of those returned silence, so the customer fell back to
   * Nominatim, which knows the avenues and not the buildings standing on them.
   *
   * `campus` is deliberately broad in `search`: a college, an institute, a department, a
   * lecture hall and an administrative office are all *"yung building sa UPLB"* to the
   * person typing, and splitting them into five chips would only make five ways to miss.
   */
  campus: { label: 'UPLB building', search: ['uplb', 'up los banos', 'elbi', 'campus', 'building', 'gusali', 'college', 'kolehiyo', 'institute', 'department', 'hall', 'office'] },

  /**
   * One chip for the residence hall on campus and the apartment block on Sapphire.
   *
   * They are different tenancies and the same errand. A customer sending food to where
   * they sleep does not care which side of the campus line it falls on, and splitting
   * *Dormitory* from *Apartment* would only make two chips to look past.
   */
  dorm: { label: 'Dorm / apartment', search: ['dorm', 'dormitory', 'dormitoryo', 'residence hall', 'apartment', 'apartments', 'boarding house', 'bahay', 'tirahan'] },
  landmark: { label: 'Landmark', search: ['landmark', 'park', 'plaza', 'monument', 'tanawin'] },

  /**
   * ## The fourth batch — where people live
   *
   * Everything above is somewhere a rider *goes to a building*. This is the answer for the
   * far commoner case, which the box could not answer at all: **the customer lives in a
   * subdivision, and that is the whole address they have.**
   *
   * *Rockville*, *Traceville*, *Umali Subdivision*, *Bay Garden* — these were reachable
   * only sideways, by typing the name of a shop that happens to stand on the same street,
   * and only where such a shop existed. Typing the subdivision itself returned silence and
   * sent the customer to the map to drop a pin by eye, which is the single hardest thing
   * this app asks anybody to do.
   *
   * The pin is the entrance, not the house. That is honest and it is enough: a rider who
   * reaches the gate of Traceville and rings the number is a rider who arrives. It is also
   * how every customer already describes where they live.
   *
   * `search` deliberately holds no street words. *Kalye* and *street* would match every
   * row in here at once and bury whatever was actually typed.
   */
  village: {
    label: 'Subdivision',
    search: ['subdivision', 'subd', 'village', 'villa', 'barangay', 'brgy', 'sitio', 'purok', 'compound'],
  },

  /**
   * The street itself, and the label is a warning as much as a name.
   *
   * A street is a line and a row here is a point, so the pin sits at its **middle**. On
   * Ruby Street that is a house or two out; on the National Highway it means very little.
   *
   * It earns its place anyway, because of what it replaces. Typing *Eldanda* returned
   * silence, and silence sends somebody to a blank map to place a pin by eye — the hardest
   * thing this app asks of anyone. A street row puts them on their own street with the map
   * already centred; nudging the pin fifty metres is a different task from finding Los
   * Baños on an empty map.
   *
   * So the chip says **Kalye** rather than borrowing a friendlier word: the customer is
   * being told they have picked a street and not a door. The pin is theirs to move, and
   * every order carries a number the rider rings.
   */
  street: {
    label: 'Kalye',
    search: ['kalye', 'street', 'kanto', 'daan', 'road', 'avenue', 'highway'],
  },

  /**
   * ## The fifth batch — the three kinds of place the sweep could not name
   *
   * These come from the OSM classes `harvest-places.js` never asked for. It queried
   * `shop=*` and thirteen amenities, which is *where a rider buys something* — so a
   * town of a hundred and thirty-five schools, churches and resorts was invisible to
   * it, not because OSM lacks them but because nothing ever asked.
   *
   * They could have gone in as `other`. They must not. The chip is **searched**, and a
   * hundred and thirty-five rows reading *Tindahan* is a hundred and thirty-five rows that
   * only somebody who already knew the exact name could find — which is the one customer
   * who did not need the listing.
   *
   * Tagalog first in `search`, for the same reason as everywhere above: nobody types
   * *place of worship*, and *simbahan* is not a translation of it but the word itself.
   */
  school: {
    label: 'Eskwelahan',
    search: ['eskwelahan', 'school', 'paaralan', 'elementary', 'high school', 'college', 'kolehiyo', 'university', 'unibersidad', 'day care'],
  },

  /**
   * One chip for the parish, the chapel, the kingdom hall and the Islamic centre.
   *
   * They are different faiths and the same errand — a rider is sent to the building and
   * the building is asked for by name. Splitting them by denomination would make eight
   * chips to look past and would put the app in the business of sorting people's religion,
   * which is not its business.
   */
  church: {
    label: 'Simbahan',
    search: ['simbahan', 'church', 'chapel', 'kapilya', 'parish', 'parokya', 'iglesia', 'mosque', 'masjid', 'moske'],
  },

  /**
   * The hot springs are the reason this chip is not called *Hotel*.
   *
   * Los Baños sells more resort nights than hotel nights and the customer says *resort*,
   * or names the pool. A chip reading *Hotel* would be read as excluding exactly the forty
   * places most likely to be typed.
   */
  hotel: {
    label: 'Hotel / resort',
    search: ['hotel', 'resort', 'inn', 'lodge', 'tuluyan', 'pahingahan', 'hot spring', 'mainit na bukal', 'pool', 'guest house', 'transient'],
  },

  /**
   * The computer shop, which is not the cellphone repair shop.
   *
   * Eighteen are mapped along Lopez and Batong Malake, seventeen of them new to the
   * listing, and every one landed under `gadget` on the first pass — a chip that reads
   * **Cellphone repair** on
   * screen, which is not what *Tambayan Computer Shop* or *Slashdot Internet Cafe* is.
   * The errand is printing, encoding, photocopying and an hour of a machine, and the
   * person with that errand never types *cellphone*.
   *
   * `pisonet` and `xerox` are in `search` because they are the words on the signage and
   * in the sentence. Nobody in Los Baños asks for a photocopy.
   */
  computer: {
    label: 'Computer shop',
    search: ['computer shop', 'internet cafe', 'net cafe', 'pisonet', 'print', 'printing', 'xerox', 'photocopy', 'encode', 'pc', 'gaming', 'computer'],
  },

  fuel: { label: 'Gas station', search: ['gas station', 'gasolinahan', 'gasolina', 'fuel', 'krudo'] },

  other: { label: 'Tindahan', search: ['tindahan', 'store'] },
}

/** Unknown keys read as `other` rather than rendering a blank chip. */
export const categoryOf = (store) => CATEGORIES[store?.category] ?? CATEGORIES.other

export const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

/**
 * The same hours seven days a week, for editing by hand.
 *
 *   hours: { ...everyDay('08:00', '22:00'), sun: null }
 *
 * Typing seven identical lines invites a typo in exactly one, and the day that gets it is
 * the day nobody notices until a rider is standing outside.
 */
export const everyDay = (opens, closes) =>
  Object.fromEntries(DAYS.map((day) => [day, { opens, closes }]))

/**
 * `[name, category, lat, lng, area, hours, alias?]` — a tuple per shop rather than an
 * object.
 *
 * 217 objects with seven keys each is sixteen hundred lines of mostly punctuation, and a
 * file nobody will scroll is a file nobody will correct. One line per shop stays readable
 * and stays greppable.
 *
 * The hours entry is `{mon:['09:00','21:00'], …}` — the pair rather than `{opens, closes}`,
 * for the same reason.
 *
 * **`alias` is optional and usually absent.** A shop is asked for by its name; a campus
 * building is asked for by an acronym nobody spells out, so the trailing slot exists for
 * the 69 rows that need it and is left off the other 271.
 */
const expand = ([storeName, category, latitude, longitude, area, hours, alias = '']) => ({
  storeName,
  category,
  latitude,
  longitude,
  area,
  alias,
  hours: hours
    ? Object.fromEntries(DAYS.map((day) => [
      day,
      hours[day] ? { opens: hours[day][0], closes: hours[day][1] } : null,
    ]))
    : null,
})

/**
 * The rows live in `./stores.json`, beside this file.
 *
 * They were inline here until 22 August 2026, when the Messenger bot needed them too: a
 * customer typing *"Puregold Los Baños"* into a chat has to become coordinates on the
 * server, and the server could not read a frontend ES module.
 *
 * ⚠️ **Each app carries its own copy, because they are separate repositories.**
 *
 * The first attempt imported the backend's copy across the folder boundary —
 * `../../../backend/src/data/stores.json`. That works on one machine and nowhere else:
 * Netlify clones *this* repo alone, so the path does not exist and the build fails. It was
 * caught the hour before a deploy, by checking `git remote` rather than by a build.
 *
 * The server needs the rows at runtime, this app needs them at build time, and neither can
 * reach the other. So there are two files and one generator:
 *
 *     node backend/scripts/sync-stores.js
 *
 * ⚠️ **Do not edit either JSON by hand.** Change the source, run the sync, commit both.
 *
 * @type {Array<[string, string, number, number, string, object|null]>}
 */
/**
 * The attribute is required, not decoration.
 *
 * Vite bundles this file either way — it resolves the JSON itself and never asks Node. The
 * test runner does ask Node, and from Node 22 an unattributed JSON import is refused
 * outright: `ERR_IMPORT_ATTRIBUTE_MISSING`, which took down the whole of `stores.test.js`
 * while every other suite passed.
 *
 * `with`, not the older `assert` — that spelling is deprecated and gone in current Node.
 */
import ROWS from './stores.json' with { type: 'json' }

/** @type {Store[]} */
export const STORES = ROWS.map(expand)

/** The shape the form's own location state speaks. */
export const asLocation = (store) => ({
  query: '',
  lat: store.latitude,
  lng: store.longitude,
  label: store.storeName,
})

/**
 * The shape a search result travels in, so a listed shop and a Nominatim hit take one path.
 *
 * `store` rides along so the picker can fill `pickupSource` from the same tap and draw the
 * category — a Nominatim row has no `store` and gets neither. `secondary` is the street,
 * and it is what turns four Jollibees into four distinguishable rows.
 */
export const asSuggestion = (store) => ({
  lat: store.latitude,
  lng: store.longitude,
  label: store.storeName,
  secondary: store.area,
  store,
})

/* ------------------------------------------------------------------------- hours */

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/

const minutesOf = (text) => {
  const match = HHMM.exec(String(text ?? '').trim())

  return match ? (Number(match[1]) * 60) + Number(match[2]) : null
}

/** A day's window, or null when the shop is shut that day or the day was never filled in. */
const windowFor = (store, day) => {
  const entry = store?.hours?.[day]
  const opens = minutesOf(entry?.opens)
  const closes = minutesOf(entry?.closes)

  return opens === null || closes === null ? null : { opens, closes }
}

/**
 * Where and when we are, in Manila, whatever the device thinks.
 *
 * A customer travelling, or a phone with its clock wrong, must not be told a shop is shut
 * when it is serving. The hours are the shop's own, so the comparison belongs in the shop's
 * timezone — and that includes the *day*, because a phone an hour ahead at 23:30 is already
 * on tomorrow's schedule.
 */
const manilaNow = (now) => {
  const parts = new Intl.DateTimeFormat('en-PH', {
    timeZone: 'Asia/Manila',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now)

  const value = (type) => parts.find((part) => part.type === type)?.value
  const weekday = String(value('weekday') ?? '').slice(0, 3).toLowerCase()

  return {
    day: DAYS.includes(weekday) ? weekday : DAYS[0],
    minute: (Number(value('hour')) * 60) + Number(value('minute')),
  }
}

const dayBefore = (day) => DAYS[(DAYS.indexOf(day) + DAYS.length - 1) % DAYS.length]

/**
 * Still open from last night.
 *
 * 01:00 on a Tuesday belongs to Monday's 18:00-02:00, not to Tuesday's. Without this a
 * shop trading through the night reads as shut for the two hours it is busiest — and 7107
 * Bar & Grill, in the list above, closes at 02:00 every day.
 */
const runningFromYesterday = (store, day, minute) => {
  const yesterday = windowFor(store, dayBefore(day))

  return yesterday !== null && yesterday.closes <= yesterday.opens && minute < yesterday.closes
}

const hasAnyHours = (store) => DAYS.some((day) => windowFor(store, day) !== null)

/**
 * Whether the shop is open right now.
 *
 * @returns {boolean|null} — **null when the hours were never set**, which is not the same
 *   as open. 180 of the 217 are in that state. Guessing "always open" sends a rider to a
 *   shuttered door at 2am and nobody learns why; guessing "closed" hides a shop that is
 *   serving. Neither is worth inventing, so the answer is *"we were not told"* and the row
 *   says nothing about hours.
 */
export function isOpenNow(store, now = new Date()) {
  if (!store?.hours) return null

  const { day, minute } = manilaNow(now)
  const today = windowFor(store, day)

  if (today === null) {
    return hasAnyHours(store) ? runningFromYesterday(store, day, minute) : null
  }

  const openToday = today.closes > today.opens
    ? minute >= today.opens && minute < today.closes
    // A shop trading past midnight has a closing time earlier than its opening one. Only
    // the evening half belongs to today.
    : minute >= today.opens

  return openToday || runningFromYesterday(store, day, minute)
}

/** `"08:00 – 22:00"` for today, `null` when today has no hours set. */
export const hoursLabel = (store, now = new Date()) => {
  const entry = store?.hours?.[manilaNow(now).day]

  return minutesOf(entry?.opens) !== null && minutesOf(entry?.closes) !== null
    ? `${entry.opens} – ${entry.closes}`
    : null
}

/* ------------------------------------------------------------------------ search */

/**
 * Accents removed, case flattened, punctuation dissolved.
 *
 * *Baños* has to be found by typing `banos`, because nobody reaches for ñ on a phone
 * keyboard. Apostrophes **vanish** rather than becoming a gap — `alings` must find *Aling
 * Glo's*, where `aling s` would be two words matching nothing. The exclamation mark goes the
 * same way — nobody types the one in *O!Save*, and a gap there leaves `osave` matching
 * nothing. Everything else that is not a letter or a digit becomes a space, so
 * `7107 bar grill` finds *7107 Bar & Grill*.
 */
const normalise = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/['\u2019`!]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

/**
 * Everything a shop can be found by: its name, its other names, its street, and the words
 * for what it is.
 */
const haystack = (store) => {
  const category = categoryOf(store)

  return normalise([store.storeName, store.alias, store.area, category.label, ...category.search].join(' '))
}

/**
 * Shops matching what the customer typed.
 *
 * **Every word has to appear, in any order.** People type the two words they remember, not
 * a registered name — `jollibee grove` narrows four branches to one, and `lopez kape`
 * finds the cafés on Lopez Avenue.
 *
 * Capped at eight rows. 217 shops means `kainan` matches every one of them, and a
 * suggestion list longer than the screen is a list nobody reads to the end of.
 *
 * @returns {Store[]} — empty when nothing matches, which is the signal to ask Google
 */
const escape = (word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * How well a shop answers what was typed, so the cap keeps the right eight.
 *
 * Substring matching alone is not enough at this size. `g store` matches every shop whose
 * text holds a *g* somewhere and the word *store* — which is most of 178 sari-saris — and
 * *G Store*, the one place actually named that, sinks below the cap and cannot be reached
 * at all.
 *
 * So a match on the **name** beats one on the street or the category, and a match at the
 * **start of a word** beats one buried inside another.
 *
 *   3 — the name begins with what was typed      `mercury` → Mercury Drug
 *   2 — every word starts a word in the name     `g store` → G Store
 *   1 — every word is in the name somewhere      `ercury`  → Mercury Drug
 *   0 — matched only through the street or the category
 *
 * **An alias scores as a name.** `ics` is not a fragment of *Institute of Computer
 * Science* — it is the whole of what the building is called — and a row it can only reach
 * at 0 sits below every campus row that merely shares the category's words.
 */
const scoreOf = (store, words, phrase) => {
  const tiers = [normalise(store.storeName), normalise(store.alias)].filter(Boolean)
    .map((text) => {
      if (text.startsWith(phrase)) return 3
      if (words.every((word) => new RegExp(`(^| )${escape(word)}`).test(text))) return 2
      if (words.every((word) => text.includes(word))) return 1
      return 0
    })

  return Math.max(...tiers)
}

/**
 * The house number in what somebody typed, if there is one.
 *
 * `9157 Emerald Street`, `Blk 4 Lot 12 Rockville`, `24 Ruby` — this is how an address is
 * actually written down, and the number is the half no listing can ever hold. Nobody has
 * recorded house 9157; what is recorded is Emerald Street.
 *
 * Recognised only at the **front**, and only as digits with an optional letter. A number in
 * the middle or at the end is part of a name — *Store 88*, *Phase 2*, *7-Eleven* — and
 * removing it would break searches that work today.
 */
const HOUSE_NUMBER = /^((?:blk|block|lot|unit|no|#)\s*)?\d+[a-z]?(?:\s*-\s*(?:\d+[a-z]?|[a-z](?![a-z])))?(\s+(?:blk|block|lot|unit)\s*\d+[a-z]?)*/i

export const houseNumberIn = (query) => {
  const text = String(query ?? '').trim()
  const match = HOUSE_NUMBER.exec(text)

  if (!match || !match[0]) return ''

  // A query that is *only* a number is not an address, it is somebody still typing.
  return text.slice(match[0].length).trim() ? match[0].trim() : ''
}

/** The same query with its house number taken off, or '' when there was none to take. */
const withoutHouseNumber = (query) => {
  const number = houseNumberIn(query)

  return number ? String(query).trim().slice(number.length).trim() : ''
}

/**
 * The words the listing has actually heard of.
 *
 * Run only on the miss path, so the cost — every remaining word against every row — is paid
 * once, by a query that was about to answer with nothing anyway.
 *
 * An empty result means the customer typed something this listing knows none of, which is
 * the right moment to stop guessing and let Nominatim answer.
 */
/**
 * Everything that answers to at least one word the listing recognises.
 *
 * The strict pass demands every word, which is right until one word is wrong — and then it
 * answers with nothing for a place it is holding. *bafabfiafafs silangan*, *po sa Ruby
 * Street*, *pakihatid po sa Traceville*: a typo, a particle, a whole sentence. All of them
 * are somebody naming a real place with words around it.
 *
 * **A word matching no row is dropped outright** — a typo or a name never recorded is not
 * evidence, and as a requirement it only erases. What is left is joined with *or* rather
 * than *and*, and the ranking sorts it out: a row answering two of the words outranks one
 * answering one, and `scoreOf` decides within each group.
 *
 * An earlier version intersected instead, most-selective word first. It read well and it
 * guessed: *nanay* turned out to be a real store and is rarer than *umali*, so *sa bahay ni
 * nanay sa umali* answered with **Nanay Onec** and no Umali at all. Choosing which word the
 * customer meant is not this function's business — offering both and ranking honestly is.
 *
 * Reached only when both stricter passes found nothing, so it can never widen an answer
 * that already worked: `jollibee grove` is settled long before this runs.
 *
 * @returns {{rows: Store[], words: string[]}}
 */
/**
 * A word carries information when it matches **some** rows and not **most** of them.
 *
 * Both ends have to be cut, and the second is the one that is easy to miss. The match is a
 * substring match — deliberately, so `enzo` finds *Enzo's* — which means **po** is inside
 * *Lo**po**z* and **sa** is inside *Sam**pa**guita*. Those are not words this listing has
 * never heard of; they are words it hears everywhere, and joining a query with them either
 * erases the answer or floods it.
 *
 * A fifth of the listing is the line. Below it a word is naming something; above it, it is
 * grammar, or a category term like *street*, and it decides nothing.
 */
const SAYS_NOTHING_ABOVE = 0.2

/**
 * Two letters is never an identifier.
 *
 * The frequency test alone lets **po** through — it is inside *Lopez*, *Poblacion*,
 * *Deposito* and 159 other rows, twelve per cent, comfortably under any sane ceiling. And
 * yet nobody has ever typed *po* to say where they live. Tagalog's particles are short by
 * nature: *po*, *sa*, *ni*, *ng*, *ay*, *ko*.
 */
const TOO_SHORT = 3

/** @returns {Map<string, number>} word → how many rows carry it, for the words worth keeping. */
function informativeWords(stores, words) {
  const ceiling = stores.length * SAYS_NOTHING_ABOVE
  const kept = new Map()

  for (const word of words) {
    if (word.length < TOO_SHORT) continue

    const hits = stores.reduce((count, store) => (haystack(store).includes(word) ? count + 1 : count), 0)

    if (hits > 0 && hits <= ceiling) kept.set(word, hits)
  }

  return kept
}

/**
 * Everything that answers to at least one of the informative words.
 *
 * The last resort, and it only runs when even an *and* over the informative words came back
 * empty — which means the customer named two things that share no row. *Sa bahay ni Nanay
 * sa Umali* is exactly that: **Nanay** is a real store here and **Umali** is a subdivision,
 * and no row is both.
 *
 * There is no right answer to guess at, so it does not guess. Both are offered, ranked by
 * how much of the query each one answers, and the customer picks — which is what a
 * suggestion list is for.
 *
 * @returns {{rows: Store[], words: string[]}}
 */
function anyKnownWord(stores, counts) {
  const words = [...counts.keys()]

  const rows = stores.filter((store) => {
    const text = haystack(store)

    return words.some((word) => text.includes(word))
  })

  return { rows, words }
}

/**
 * The rarer the word a row answers to, the higher it sits.
 *
 * Without this the loose pass is a coin toss. *bahay ko sa Rockville* leaves **bahay** (65
 * rows) and **rockville** (2); every row answers exactly one of them, so the tiebreak falls
 * through to alphabetical and the customer is offered *4 Boys House* above the subdivision
 * they just named.
 *
 * Weighting by `1 / hits` says the obvious thing out loud: a word carried by two rows names
 * a place, and one carried by sixty-five describes a kind of place. Both are still offered
 * — the specific one leads.
 */
const rarityScore = (store, counts) => {
  const text = haystack(store)
  let score = 0

  for (const [word, hits] of counts) {
    if (text.includes(word)) score += 1 / hits
  }

  return score
}

function search(stores, query) {
  const phrase = normalise(query)
  const words = phrase.split(/\s+/).filter(Boolean)

  if (words.length === 0) return []

  return stores.filter((store) => {
    const text = haystack(store)
    return words.every((word) => text.includes(word))
  })
}

export function matchIn(stores, query) {
  const phrase = normalise(query)
  const words = phrase.split(/\s+/).filter(Boolean)

  if (words.length === 0) return []

  /**
   * The whole query first, and the street on its own only when that found nothing.
   *
   * A second pass rather than always stripping the number, because the number is usually
   * meaningful: *7-Eleven*, *Petron 2*, *Phase 2* all match today and must keep matching.
   * The fallback runs only where the strict answer was silence — which is precisely the
   * case somebody typing their own address falls into, every time.
   *
   * `9157 Emerald Street` finds Emerald Street. The 9157 is not thrown away: the picker
   * puts it back on the label, so the rider reads the address the customer actually wrote.
   * See `useLocationSearch.selectSuggestion`.
   */
  /**
   * Three passes, each one only reached because the one before it found nothing.
   *
   * **1 — the whole query.** Every word has to appear, which is what makes typing a second
   * word *narrow* rather than widen: `jollibee grove` is one branch out of four, and
   * `lopez kape` is the cafés on Lopez Avenue. This is the answer almost every time.
   *
   * **2 — without the house number.** `9157 Emerald Street` is an address; 9157 is in no
   * listing on earth. See `houseNumberIn`.
   *
   * **3 — without the words that match nothing at all.** A word that appears in *no row*
   * cannot be narrowing anything — it can only be erasing. `bafabfiafafs silangan` returned
   * silence for a street the listing was holding, and so does every real version of that:
   * a misspelling, a barangay spelled another way, a *po* or a *sa* that came along with
   * the address.
   *
   * Note what this deliberately does **not** do: match on *any* word. That would undo pass
   * 1 entirely — `jollibee grove` would answer with every Jollibee and everything on Grove,
   * and the customer would be worse off for having typed the second word. A word is dropped
   * only when the listing has never heard of it, which is exactly when it cannot be the
   * word doing the work.
   */
  let asked = String(query)
  let found = search(stores, asked)
  /** Set only by the loose pass, which is the only one that needs it to rank at all. */
  let rarity = null

  if (!found.length) {
    const stripped = withoutHouseNumber(asked)

    if (stripped) {
      asked = stripped
      found = search(stores, asked)
    }
  }

  if (!found.length) {
    /**
     * The words that were carrying the meaning, and only those.
     *
     * *pakihatid po sa traceville* becomes *traceville*; *9157 emerald street xyzzy* becomes
     * *emerald*. Still an **and** — two informative words both have to appear — so this
     * narrows exactly as pass 1 does, having thrown away only what could not narrow.
     */
    const carrying = informativeWords(stores, normalise(asked).split(/\s+/).filter(Boolean))

    if (carrying.size) {
      asked = [...carrying.keys()].join(' ')
      found = search(stores, asked)
    }

    // And when even those share no row — *Nanay* is a store, *Umali* is a subdivision — the
    // list offers both rather than choosing for the customer, rarest word leading.
    if (!found.length && carrying.size > 1) {
      const loose = anyKnownWord(stores, carrying)

      found = loose.rows
      asked = loose.words.join(' ')
      rarity = carrying
    }
  }

  /**
   * Ranked by the words that actually did the finding, not the ones that were typed.
   *
   * `24 Ruby Street` matched on *ruby street*, so *ruby street* is what the ranking has to
   * score against. Scoring the original — house number and all — makes every row score
   * identically, the tiebreak falls through to alphabetical, and the answer is *Aby & AJ
   * Store* above the street the customer named.
   */
  const rankPhrase = normalise(asked)
  const rankWords = rankPhrase.split(/\s+/).filter(Boolean)

  /**
   * A shop that is shut sinks, but is never hidden.
   *
   * Hiding it answers *"where is Jollibee?"* with silence, which reads as a broken search —
   * and the form has a scheduling step, so ordering now from a shop that opens at eight is
   * a real thing to want. Unknown hours rank with the open ones: 180 shops have none, and
   * sinking all of them would sink most of the list.
   */
  return [...found]
    .sort((a, b) =>
      /**
       * Only the loose pass sets `rarity`, and there it is the whole ranking: the row
       * answering the rarest word the customer typed is the place they named. Null on every
       * other path, so nothing above this line changes for a query that matched normally.
       */
      (rarity ? rarityScore(b, rarity) - rarityScore(a, rarity) : 0)
      // Shut sinks, but is never hidden — see above.
      || (Number(isOpenNow(b) !== false) - Number(isOpenNow(a) !== false))
      || (scoreOf(b, rankWords, rankPhrase) - scoreOf(a, rankWords, rankPhrase))
      // Deterministic, so the same query never returns two different eights.
      || a.storeName.localeCompare(b.storeName, 'en'))
    .slice(0, 8)
}

/** The same search, over the list that actually ships. */
export const matchStores = (query) => matchIn(STORES, query)
